import { gql, useMutation, useSuspenseQuery } from '@apollo/client';
import { useForm } from 'react-hook-form';
import { Button } from 'components/button';
import { Dropdown } from 'components/dropdown-v7';
import { Mermaid } from 'components/mermaid';
import type {
  AddSequenceToDosageScheduleMutation,
  AddSequenceToDosageScheduleMutationVariables,
  DosageScheduleAddSequenceQuery,
  VariantSequenceNode,
} from 'graphql/types';
import { useNotifications } from 'notifications';
import { DosageScheduleMermaid, formatDoseUnit } from './mermaid';
import { useHistory, useParams } from 'react-router-dom';
import { buildRoute } from 'utils/routes';

const dosageScheduleAddSequenceQuery = gql`
  query DosageScheduleAddSequence(
    $id: ID!
    $selectedSequenceId: ID!
    $sequenceSelected: Boolean!
  ) {
    dosageSchedule(id: $id) {
      id
      displayName
      internalName
      maxDaysSinceDoseBeforeIntervention
      minSafeDoseIntervalDays
      sequences {
        id
        internalName
      }
      doses {
        id
        intervalDays
        nextDosageScheduleEventId
        variant {
          id
          name
        }
        dose {
          id
          medicineTradeName
          quantity
          unit
        }
      }
    }
    sequences(problemTypes: [WEIGHT_LOSS], requiresPrescription: true) {
      id
      internalName
      status
    }
    sequence(id: $selectedSequenceId) @include(if: $sequenceSelected) {
      id
      mermaid
      nodes {
        id
        ... on VariantSequenceNode {
          variant {
            id
            name
            product {
              id
              productType
            }
          }
        }
      }
    }
  }
`;

type AddSequenceToDosageScheduleForm = {
  dosageScheduleId: string;
  sequenceId: string;
  links?: Array<{
    scheduleDoseId: string;
    variantNodeId: string;
  }>;
};

const AddSequencePage = () => {
  const { dosageScheduleId } = useParams<{ dosageScheduleId: string }>();
  const showNotification = useNotifications();
  const history = useHistory();

  const { register, handleSubmit, formState, control, watch } =
    useForm<AddSequenceToDosageScheduleForm>({
      reValidateMode: 'onChange',
    });

  const sequenceId = watch('sequenceId');

  const { data } = useSuspenseQuery<DosageScheduleAddSequenceQuery>(
    dosageScheduleAddSequenceQuery,
    {
      variables: {
        id: dosageScheduleId,
        selectedSequenceId: sequenceId || '',
        sequenceSelected: !!sequenceId,
      },
    },
  );

  const [addSequenceToDosageSchedule] = useMutation<
    AddSequenceToDosageScheduleMutation,
    AddSequenceToDosageScheduleMutationVariables
  >(
    gql`
      mutation AddSequenceToDosageSchedule(
        $input: AddSequenceToDosageScheduleInput!
      ) {
        addSequenceToDosageSchedule(input: $input) {
          dosageSchedule {
            id
            sequences {
              id
            }
          }
        }
      }
    `,
    {
      onCompleted: () => {
        showNotification({
          type: 'success',
          message: 'Sequence added to dosage schedule',
        });
        history.push(buildRoute.dosageSchedule(dosageScheduleId));
      },
      onError: () => {
        showNotification({
          type: 'error',
          message: 'Error adding sequence to dosage schedule',
        });
      },
    },
  );

  const dosageSchedule = data.dosageSchedule;
  if (!dosageSchedule) {
    return <div className="font-medium pt-8">Dosage schedule not found</div>;
  }

  return (
    <div className="bg-white rounded-md">
      <div className="p-6 border-b border-slate-200">
        <h3 className="heading-md">Add Sequence to Schedule</h3>
      </div>

      <form
        className="flex-1 min-h-0"
        onSubmit={handleSubmit(async (formValues): Promise<void> => {
          await addSequenceToDosageSchedule({
            variables: {
              input: {
                dosageScheduleId: dosageScheduleId,
                sequenceId: formValues.sequenceId,
                links:
                  formValues.links?.map((link) => ({
                    scheduleDoseId: link.scheduleDoseId,
                    variantNodeId: link.variantNodeId,
                  })) ?? [],
              },
            },
          });
        })}
      >
        <div className="flex-1 flex min-h-0">
          <div className="w-7/12 flex flex-col border-r border-slate-200 h-full">
            <div className="flex-1 flex flex-col min-h-0 border-slate-200">
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                <h4 className="text-lg leading-6 font-medium text-slate-900">
                  Sequence
                </h4>
              </div>
              <div className="p-4">
                <Dropdown
                  {...register('sequenceId', { required: true })}
                  name="sequenceId"
                  menuPlacement="bottom"
                  control={control}
                  options={(data?.sequences ?? [])
                    .filter(
                      (s) =>
                        s.status === 'AVAILABLE' &&
                        !dosageSchedule.sequences?.some((sd) => sd.id === s.id),
                    )
                    .map((s) => ({
                      label: s.internalName,
                      value: s.id,
                    }))}
                />
              </div>
              <div className="flex-1 p-4 overflow-auto">
                {data.sequence?.mermaid ? (
                  <Mermaid key={sequenceId}>{data.sequence?.mermaid}</Mermaid>
                ) : (
                  <div className="flex items-center justify-center h-full text-slate-500">
                    Select a sequence to add to the schedule
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 flex flex-col min-h-0 border-t border-slate-200">
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                <h4 className="text-lg leading-6 font-medium text-slate-900">
                  Dosage Schedule
                </h4>
              </div>
              <div className="flex-1 p-4 overflow-auto">
                <DosageScheduleMermaid dosageSchedule={dosageSchedule} />
              </div>
            </div>
          </div>

          <div className="w-5/12 flex flex-col h-full">
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
              <h4 className="text-lg leading-6 font-medium text-slate-900">
                Map Variant Nodes to Schedule Doses
              </h4>
            </div>
            <div className="flex-1 p-4 space-y-4">
              <div className="flex gap-4 items-center">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-slate-700">
                    Variant Node
                  </label>
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-slate-700">
                    Schedule Dose
                  </label>
                </div>
              </div>
              {dosageSchedule.doses.map((dose, index) => (
                <div key={dose.id} className="flex gap-4 items-start">
                  <div className="flex-1">
                    <Dropdown
                      {...register(`links.${index}.variantNodeId`, {
                        required: 'Variant node is required',
                      })}
                      name={`links.${index}.variantNodeId`}
                      control={control}
                      options={(data.sequence?.nodes ?? [])
                        .filter(
                          (n): n is VariantSequenceNode =>
                            n.__typename === 'VariantSequenceNode' &&
                            n.variant?.product.productType === 'RX',
                        )
                        .map((n) => ({
                          label: `${n.variant?.name} (${n.id.slice(-6)})`,
                          value: n.id,
                        }))}
                      errorMessage={
                        formState.errors.links?.[index]?.variantNodeId?.message
                      }
                    />
                    <input
                      type="hidden"
                      {...register(`links.${index}.scheduleDoseId`)}
                      value={dose.id}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-md">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-900">
                          {dose.dose?.medicineTradeName}
                          <span className="text-slate-500">
                            {' '}
                            ({dose.dose?.quantity ?? '-'}{' '}
                            {formatDoseUnit(dose.dose?.unit)})
                          </span>
                        </span>
                        <span className="text-xs text-slate-500 font-mono">
                          {dose.id.slice(-6)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200 p-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              history.push(buildRoute.dosageSchedule(dosageScheduleId));
            }}
          >
            Cancel
          </Button>
          <Button type="submit">Add Sequence</Button>
        </div>
      </form>
    </div>
  );
};

export default AddSequencePage;
